import React, { useState } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Button,
  Collapse,
  ListItem,
  makeStyles
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    borderRadius: 0,
    '&:hover': {
      borderRadius: 0,
    }
  },
  buttonLeaf: {
    color: theme.palette.text.secondary,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightMedium,
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium
    },
    '&.depth-0': {
      '& $title': {
        fontWeight: theme.typography.fontWeightMedium
      }
    }
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary
  },
  title: {
    marginRight: 'auto',
    color: theme.palette.text.primary,
    marginLeft: 10,
    paddingTop: 2,
    ...theme.typography.body2,
  },
  active: {
    color: theme.palette.text.primary,
    borderRadius: 0,
    backgroundColor: theme.palette.menu.light,
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.primary
    },
    '& $icon': {
      color: theme.palette.text.primary
    },
    '&:hover': {
      backgroundColor: theme.palette.menu.main,
    }
  }
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  link,
  icon: Icon,
  open: openProp,
  title,
  img,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);
  const location = useLocation();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 20;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          className={classes.button}
          onClick={handleToggle}
          style={style}
        >
          {Icon && (
            <Icon
              className={classes.icon}
              size="18"
            />
          )}
          <span className={classes.title}>
            {title}
          </span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={open}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  const onClickOpen = (link, blank) => {
    if (blank) {
      window.open(link);
    } else {
      window.location.href = link;
    }
  }

  return (
    <ListItem
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      key={title}
      {...rest}
    >
      {link ? (
        <Button
          activeClassName={classes.active}
          className={
            (
              (
                location.pathname.includes('/erp/quotation') 
                && href === '/erp/quotation'
              ) || 
              (
                location.pathname.includes('/erp/customer') 
                && href === '/erp/customer'
              )
            )
            ? clsx(classes.buttonLeaf, classes.active)
            : classes.buttonLeaf
          }
          exact
          style={style}
          onClick={() => onClickOpen(link, href === '/erp/user-manual')}
        >
          {Icon && (
            <Icon
              className={classes.icon}
              size="18"
            />
          )}
          <span className={classes.title}>
            {title}
          </span>
        </Button>
      ) : (
        <Button
          activeClassName={classes.active}
          className={
            (
              (
                location.pathname.includes('/erp/quotation') 
                && href === '/erp/quotation'
              ) || 
              (
                location.pathname.includes('/erp/customer') 
                && href === '/erp/customer'
              )
            )
            ? clsx(classes.buttonLeaf, classes.active)
            : classes.buttonLeaf
          }
          component={RouterLink}
          exact
          style={style}
          to={href}
        >
          {Icon && (
            <Icon
              className={classes.icon}
              size="18"
            />
          )}
          <span className={classes.title}>
            {title}
          </span>
        </Button>
      )}
      
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired
};

NavItem.defaultProps = {
  open: false
};

export default NavItem;
